import { inject, Injectable, signal } from '@angular/core';
import { PrizesFields, PrizesForm, PrizesFormGroups, PrizesValidators } from '../models/fidelity-prize.model';
import { CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { Validators } from '@angular/forms';
import { FidelityDataCatalogService } from './fidelity-data-catalog.service';
import { Items } from 'tilby-models';
import { ConfigurationManagerService } from 'src/app/core';
const moment = require('moment-timezone');

@Injectable()
export class FidelityPrizeFormService {

  private readonly fidelityDataCatalogService = inject(FidelityDataCatalogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);

  private prizeForm!: PrizesFormGroups;
  
  get form() {
    return this.prizeForm;
  }

  itemsMap: Record<string, Items> = {};
  prizeFormReady = signal(false);

  validators: PrizesValidators = {
    nameMaxLength: 255,

    // --------
    provMinLength: 0,
    provMaxLength: 255,
    provMaxLengthArray: new Array(10).fill(2)
  };

  createForm(prize: any, hiddenFields?:Array<keyof PrizesFields>) {
    this.prizeFormReady.set(false);
    // only read to find the name of the linked item: no need to copy the whole item map on every selection
    this.itemsMap = (this.fidelityDataCatalogService.peekDataByKey('itemsMap') ?? {}) as Record<string, Items>;
    this.prizeForm = new CustomFormGroup<PrizesForm>({
      id: new CustomFormControl(
        prize?.id || null
      ),
      name: new CustomFormControl(
        prize?.name || null,
        { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength)] }
      ),
      points: new CustomFormControl(
        prize?.points || null,
        { validators: [Validators.required] }
      ),
      type: new CustomFormControl(
        prize?.type || 'discount_fix',
        { validators: [Validators.required] }
      ),
      discount_amount: new CustomFormControl(
        prize?.discount_amount || null
      ),
      item_sku: new CustomFormControl(
        prize?.item_sku || null
      ),
      item_name: new CustomFormControl(
        prize?.item_sku? this.itemsMap[prize.item_sku]?.name : null
      ),
      campaign_id: new CustomFormControl(
        prize?.campaign_id || null,
        { validators: [Validators.required] }
      ),
      valid_from: new CustomFormControl(
        prize?.valid_from || null
      ),
      valid_to: new CustomFormControl(
        prize?.valid_to || null
      ),
    });

    this.prizeFormReady.set(true);
    return this.prizeForm;
  }
  
  prepareDataForSave(): any {
    const formValue = this.prizeForm.getRawValue();
    const prizeToSend = structuredClone(formValue);

    // prizeToSend.valid_from = formValue.valid_from ? new Date(formValue.valid_from).toISOString() : null;
    // prizeToSend.valid_to = formValue.valid_to ? new Date(formValue.valid_to).toISOString() : null;

    const timezone = this.configurationManagerService.getPreference('general.timezone');
    if (formValue.valid_from) {
      prizeToSend.valid_from = moment.tz(formValue.valid_from, timezone).format();
    } else {
      prizeToSend.valid_from = null;
    }

    if (formValue.valid_to) {
      prizeToSend.valid_to = moment.tz(formValue.valid_to, timezone).format();
    } else {
      prizeToSend.valid_to = null;
    }

    if (prizeToSend.type === 'gift') {
      prizeToSend.discount_amount = null;
    } else {
      prizeToSend.item_sku = null;
    }

    delete prizeToSend.item_name;
    delete prizeToSend._typeString;

    return prizeToSend;
  }

  get id() {
    return this.prizeForm.controls.id;
  }

  get name() {
    return this.prizeForm.controls.name;
  }

  get points() {
    return this.prizeForm.controls.points;
  }

  get type() {
    return this.prizeForm.controls.type;
  }

  get discount_amount() {
    return this.prizeForm.controls.discount_amount;
  }

  get item_sku() {
    return this.prizeForm.controls.item_sku;
  }

  get item_name() {
    return this.prizeForm.controls.item_name;
  }

  get campaign_id() {
    return this.prizeForm.controls.campaign_id;
  }

  get valid_from() {
    return this.prizeForm.controls.valid_from;
  }

  get valid_to() {
    return this.prizeForm.controls.valid_to;
  }
}
